import { memo, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Check, ChevronLeft, ChevronRight, ExternalLink, Link as LinkIcon } from 'lucide-react';
import { useProjects } from '@/hooks/use-content';
import type { ProjectRow as Project } from '@/types/database';
import { generateProjectSlug } from '@/lib/slug';
import SectionEyebrow from './SectionEyebrow';

// Skeleton shaped like the real card (image area + text lines) instead of a
// single flat block, so the layout doesn't jump when real content replaces it.
const ProjectCardSkeleton = () => (
  <div className="rounded-lg overflow-hidden border border-border bg-card">
    <Skeleton className="aspect-[9/16] sm:aspect-[3/4] rounded-none" />
    <div className="p-6 space-y-3">
      <Skeleton className="h-5 w-2/3" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-4/5" />
    </div>
  </div>
);

const ProjectCard = memo(({ project, onProjectClick }: { project: Project; onProjectClick: (project: Project) => void }) => {
  const { t } = useTranslation();
  const [currentImage, setCurrentImage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setIsLoading(true);
  }, [currentImage]);

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImage((prev) => (prev + 1) % project.screens.length);
  };

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImage((prev) => (prev - 1 + project.screens.length) % project.screens.length);
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}/projects/${generateProjectSlug(project.title)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard access denied — nothing meaningful to show inline here without
      // a toast, so just leave the icon as-is; the user can copy from the address
      // bar after opening the project instead.
    }
  };

  return (
    <Card
      className="overflow-hidden hover:shadow-card transition-all duration-300 group cursor-pointer"
      onClick={() => onProjectClick(project)}
    >
      <div className="relative aspect-[9/16] sm:aspect-[3/4] bg-muted overflow-hidden">
        {isLoading && <Skeleton className="absolute inset-0 w-full h-full rounded-none" />}
        <img
          src={project.screens[currentImage]}
          alt={`${project.title} screenshot ${currentImage + 1}`}
          className={`w-full h-full object-contain transition-transform duration-300 group-hover:scale-105 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
          onLoad={() => setIsLoading(false)}
          onError={(e) => {
            setIsLoading(false);
            e.currentTarget.src = "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&h=600&fit=crop";
          }}
        />

        {project.screens.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-card/80 backdrop-blur-sm p-2 rounded-full hover:bg-card transition-colors"
              aria-label={t('projects.previousImageAria')}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-card/80 backdrop-blur-sm p-2 rounded-full hover:bg-card transition-colors"
              aria-label={t('projects.nextImageAria')}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
              {project.screens.map((_, idx) => (
                <div
                  key={idx}
                  className={`h-1.5 rounded-full transition-all ${idx === currentImage ? 'w-6 bg-primary' : 'w-1.5 bg-card/60'}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="p-6">
        <div className="flex items-start justify-between gap-2 mb-3">
          <h3 className="text-xl font-semibold">{project.title}</h3>
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={handleShare}
              className="text-muted-foreground hover:text-primary transition-colors"
              aria-label={t('projects.copyLinkAria', { title: project.title })}
            >
              {copied ? <Check className="h-5 w-5 text-primary" /> : <LinkIcon className="h-5 w-5" />}
            </button>
            {project.link && (
              <a
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-primary hover:text-primary/80 transition-colors"
                aria-label={t('projects.openLinkAria', { title: project.title })}
              >
                <ExternalLink className="h-5 w-5" />
              </a>
            )}
          </div>
        </div>
        <p className="text-muted-foreground text-sm leading-relaxed">{project.description}</p>
      </div>
    </Card>
  );
});
ProjectCard.displayName = 'ProjectCard';

const Projects = ({ onProjectClick }: { onProjectClick: (project: Project) => void }) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const { data: projectsData, isLoading } = useProjects();

  // Show only 12 projects initially
  const visibleProjects = !projectsData ? [] : showAll ? projectsData : projectsData.slice(0, 12);

  return (
    <section id="projects" className="py-14 sm:py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <SectionEyebrow index="03" label={t('projects.eyebrow')} />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('projects.title')}</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            <Trans i18nKey="projects.description" components={{ b: <strong /> }} />
          </p>
        </div>

        {/* Project grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {isLoading
            ? Array.from({ length: 8 }).map((_, idx) => <ProjectCardSkeleton key={idx} />)
            : visibleProjects.map((project) => (
                <ProjectCard key={project.id} project={project} onProjectClick={onProjectClick} />
              ))}
        </div>

        {/* Show more button */}
        {projectsData && projectsData.length > 12 && (
          <div className="text-center mt-10">
            <Button
              size="lg"
              className="bg-gradient-primary hover:scale-[1.03] transition-all shadow-lg"
              onClick={() => setShowAll(!showAll)}
              aria-label={showAll ? t('projects.showLessAria') : t('projects.showMoreAria')}
            >
              {showAll ? t('projects.showLess') : t('projects.showMore')}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Projects;
